import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { redeemEventAccess } from '../services/api';

/**
 * Landing page for a private event's QR code / share link.
 *
 * The `t` query parameter is the event's share token: exchanging it for a
 * lasting grant is what lets a guest past the private-event guard. The token
 * has to survive the login redirect, so it is stashed alongside the pending
 * destination and replayed once the guest comes back authenticated.
 */
const EventAccess = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading } = useAuth();
  const destination = `/event/${eventId}/browse`;
  const tokenStorageKey = `pendingEventToken:${eventId}`;

  const readStoredToken = () => {
    try {
      return sessionStorage.getItem(tokenStorageKey) || '';
    } catch {
      return '';
    }
  };

  const token = searchParams.get('t') || readStoredToken();
  const [redeeming, setRedeeming] = useState(Boolean(token));
  const [error, setError] = useState('');

  const clearStoredToken = useCallback(() => {
    try {
      sessionStorage.removeItem(tokenStorageKey);
    } catch {
      /* sessionStorage can be unavailable in private browsing */
    }
  }, [tokenStorageKey]);

  // After signing in the guest must land back here rather than on /browse:
  // returning straight to /browse would skip redeeming the token and hit the
  // private-event guard all over again.
  const returnPath = token ? `/event/${eventId}/access?t=${encodeURIComponent(token)}` : destination;

  useEffect(() => {
    if (loading || isAuthenticated) return;

    // Not signed in yet: keep both the return path and the token so the link
    // still works after the guest logs in or registers.
    try {
      sessionStorage.setItem('pendingEventRedirect', returnPath);
      if (token) sessionStorage.setItem(tokenStorageKey, token);
    } catch {
      /* ignore: the token stays in the URL for this attempt */
    }
  }, [isAuthenticated, loading, returnPath, token, tokenStorageKey]);

  useEffect(() => {
    if (loading) return;

    // A guest arriving signed-out has nothing to redeem yet; clear the flag so
    // the render falls through to the login redirect instead of spinning.
    if (!isAuthenticated) {
      setRedeeming(false);
      return;
    }

    // No token means either a public event or a link that lost its parameter.
    // Send them through anyway and let the browse page report what it finds.
    if (!token) {
      setRedeeming(false);
      return;
    }

    let cancelled = false;

    const redeem = async () => {
      setRedeeming(true);
      try {
        await redeemEventAccess(eventId, token);
        if (!cancelled) clearStoredToken();
      } catch (err) {
        if (!cancelled) setError(err.message || "Ce lien d'accès n'est pas valide.");
      } finally {
        if (!cancelled) setRedeeming(false);
      }
    };

    redeem();

    return () => {
      cancelled = true;
    };
  }, [clearStoredToken, eventId, isAuthenticated, loading, token]);

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  // Checked before the redeeming spinner: a signed-out guest must always reach
  // the login page, whatever the redeem flag happens to hold.
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: { pathname: returnPath }, eventAccessFrom: location }} replace />;
  }

  if (redeeming) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Vérification de votre lien...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="error-message">{error}</div>
        <p>Demandez un nouveau lien au photographe de l'événement.</p>
      </div>
    );
  }

  return <Navigate to={destination} replace />;
};

export default EventAccess;
